import React, { useState, useEffect, useRef, useMemo } from "react";
import SearchBar from "../explore/searchBar/searchBar";
import { Link } from "react-router5";
import { useTranslation } from "react-i18next";

function settings_is_focused(id, focus) {
  return (focus[id] || {}).focus_enabled;
}

function settings_is_notification(id, focus) {
  return (focus[id] || {}).notification_enabled;
}

// traverse the tree
// return a list of item matching or true if all matches
const mayFilterSearch = (data_source, search) => {
  search = search.toLowerCase();
  let search_list = [];

  if (search === "") return true;

  const saveOnMatch = (theme) => {
    if (theme.item.toLowerCase().includes(search)) {
      search_list.push(theme);
    }
  };

  // traverse data_source and register who need it
  data_source.forEach((root) => {
    saveOnMatch(root);
    root.children.forEach((subroot) => {
      saveOnMatch(subroot);
      subroot.children.forEach(saveOnMatch);
    });
  });

  return search_list;
};

// this is not a tree
// calling filter on every node
// filter takes the node + type of node (root|subroot|leaf)
function filterTree(data_source, filter) {
  return data_source
    .filter((root) => filter(root, "root"))
    .map((root) => {
      const subroots = root.children
        .filter((subroot) => filter(subroot, "subroot"))
        .map((subroot) => {
          const leaves = subroot.children.filter((leaf) =>
            filter(leaf, "leaf"),
          );

          return { ...subroot, children: leaves };
        });
      return { ...root, children: subroots };
    });
}

// filter data, keeping all ancestors if a node is to be diplayed
const mayFilterData = (data_source, focus_list, search_list) => {
  // no filter
  if (focus_list === true && search_list === true) return data_source;

  let build_from;

  if (focus_list === true) {
    build_from = search_list;
  } else if (search_list === true) {
    build_from = focus_list;
  } else {
    // search_list is restricted to those already in focus
    // FIXME: bug, search_list may refer to a root item that is to be shown even if not in focus
    build_from = focus_list.filter((a) =>
      search_list.find((b) => a.id === b.id),
    );
  }

  let ids = {};
  build_from.forEach((x) => {
    ids[x.root] = true;
    ids[x.subroot] = true;
    ids[x.id] = true;
  });

  const filter = (node, type) => {
    return ids[node.id];
  };

  return filterTree(data_source, filter);
};

// filter by search and then by focus
const filterThemeTree = (
  themes,
  search,
  focus,
  display_mode,
  active_display_list,
) => {
  const maybe_search_list = mayFilterSearch(themes, search || "");
  const maybe_focus_list =
    display_mode === "focus_only" || active_display_list !== true
      ? focus.concat(active_display_list === true ? [] : active_display_list)
      : true;
  return mayFilterData(themes, maybe_focus_list, maybe_search_list);
};

function ThemeCard({ theme, toggleStar, toggleNotification, focus }) {
  const starValue = (c) =>
    c.new_star !== undefined ? c.new_star : settings_is_focused(c.id, focus);
  const notificationValue = (c) =>
    c.new_notification !== undefined
      ? c.new_notification
      : settings_is_notification(c.id, focus);

  return (
    <div className="focus-theme-card">
      <div className="focus-theme-card-root-head">
        <Link
          routeName={"explore.theme"}
          routeOptions={{ reload: true }}
          routeParams={{ id: theme.id }}
          className="link-theme"
        >
          <div className="focus-theme-item">{theme.item}</div>
        </Link>
        {theme.confidential && <div className="lock-icon-focus" />}
        <div
          className={"focus-star" + (starValue(theme) ? " active" : "")}
          onClick={() => toggleStar(theme)}
        />
        <div
          className={
            "notify-icon" + (notificationValue(theme) ? " active" : "")
          }
          onClick={() => toggleNotification(theme)}
        />
      </div>
      <div className="focus-theme-card-root-children">
        {theme.children.map((subtheme) => {
          return (
            <div key={subtheme.id} className="focus-theme-card-subroot">
              <div className="focus-theme-card-subroot-head">
                <div className="theme-dash" />
                <Link
                  routeName={"explore.theme"}
                  routeOptions={{ reload: true }}
                  routeParams={{ id: subtheme.id }}
                  className="link-theme"
                >
                  <div className="focus-theme-item">{subtheme.item}</div>
                </Link>
                {subtheme.confidential && <div className="lock-icon-focus" />}
                <div
                  className={
                    "focus-star" + (starValue(subtheme) ? " active" : "")
                  }
                  onClick={() => toggleStar(subtheme)}
                />
                <div
                  className={
                    "notify-icon" +
                    (notificationValue(subtheme) ? " active" : "")
                  }
                  onClick={() => toggleNotification(subtheme)}
                />
              </div>
              <div className="focus-theme-card-subroot-children">
                {subtheme.children.map((leaf) => {
                  return (
                    <div key={leaf.id} className="focus-theme-card-leaf-head">
                      <div className="theme-dash" />
                      <Link
                        routeName={"explore.theme"}
                        routeOptions={{ reload: true }}
                        routeParams={{ id: leaf.id }}
                        className="link-theme"
                      >
                        <div className="focus-theme-item">{leaf.item}</div>
                      </Link>
                      {leaf.confidential && <div className="lock-icon-focus" />}
                      <div
                        className={
                          "focus-star" + (starValue(leaf) ? " active" : "")
                        }
                        onClick={() => toggleStar(leaf)}
                      />
                      <div
                        className={
                          "notify-icon" +
                          (notificationValue(leaf) ? " active" : "")
                        }
                        onClick={() => toggleNotification(leaf)}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// mix minimum and maximum items to rearrange vertically
const reorderThemes = (themes) => {
  // sort by most items
  const xs = themes
    .map((x) => {
      let count = x.children.length;
      x.children.forEach((x) => (count += x.children.length));

      return { theme: x, count };
    })
    .sort((a, b) => a.count > b.count)
    .map((x) => x.theme);

  // arrange so that we take the biggest then the smallest
  return xs.slice(0, Math.ceil(xs.length / 2)).flatMap((x, i) => {
    const x2 = xs[xs.length - i - 1];
    return x === x2 ? [x] : [x, x2];
  });
};

function traverseTree(theme_tree, cb) {
  // traverse and test for star update
  return theme_tree.map((root) => {
    const children = root.children.map((subroot) => {
      const children = subroot.children.map(cb);
      return cb({ ...subroot, children });
    });

    return cb({ ...root, children });
  });
}

function flattenTree(theme_tree) {
  return theme_tree.flatMap((root) => {
    return [root].concat(
      root.children.flatMap((subroot) => {
        return [subroot].concat(subroot.children);
      }),
    );
  });
}

// traverse the tree, return a list of theme (being focused or notified)
const idsToThemes = (theme_tree, focus) => {
  let found = [];
  const addIfFound = (theme) => {
    if (
      settings_is_focused(theme.id, focus) ||
      settings_is_notification(theme.id, focus)
    ) {
      found.push(theme);
    }
  };

  theme_tree.forEach((root) => {
    addIfFound(root);
    root.children.forEach((subroot) => {
      addIfFound(subroot);
      subroot.children.forEach((leaf) => addIfFound(leaf));
    });
  });

  return found;
};

// https://fr.reactjs.org/docs/hooks-effect.html
// https://tobiasahlin.com/blog/masonry-with-css/
function ThemeFocus({ themes, focus, notify, mode, search, setSearch }) {
  const { t } = useTranslation();
  const ref = useRef();

  const [active_display_list, setActiveDisplayList] = React.useState(true);

  const focus_theme_list = useMemo(() => {
    return idsToThemes(themes, focus);
  }, [themes, focus]);

  // themes_s is modified w/ star being added to it
  const [themes_s, updateThemes] = useState(() => {
    return reorderThemes(themes);
  });

  useEffect(() => {
    const { current } = ref;
    const total_height = current.offsetHeight;
    const max_height = (total_height / 2) * 1.3; // add a margin of 1/3

    current.style.maxHeight = max_height + "px";
  }, []);

  const toggleStar = (theme) => {
    if (!mode.editable) return;

    const starValue = (c) =>
      c.new_star !== undefined ? c.new_star : settings_is_focused(c.id, focus);

    const new_themes = traverseTree(themes_s, (current) => {
      return current.id === theme.id
        ? { ...current, new_star: !starValue(current) }
        : current;
    });

    const theme_list = flattenTree(new_themes);

    notify(theme_list);
    updateThemes(new_themes);
  };

  const toggleNotification = (theme) => {
    if (!mode.editable) return;

    const notificationValue = (c) =>
      c.new_notification !== undefined
        ? c.new_notification
        : settings_is_notification(c.id, focus);

    const new_themes = traverseTree(themes_s, (current) => {
      return current.id === theme.id
        ? { ...current, new_notification: !notificationValue(current) }
        : current;
    });

    const theme_list = flattenTree(new_themes);

    notify(theme_list);
    updateThemes(new_themes);
  };

  function toggleDisplay() {
    if (mode === "show") return;

    if (active_display_list !== true) {
      setActiveDisplayList(true);
    } else {
      let active_display_list = [];
      traverseTree(themes_s, (current) => {
        if (current.new_star || current.new_notification) {
          active_display_list.push(current);
        }
        return current;
      });

      setActiveDisplayList(active_display_list);
    }
  }

  let view;

  if (mode.editable) {
    view = (
      <div>
        <div className="c-showedit_selectAll">
          <input
            type="checkbox"
            style={{ cursor: "pointer", marginRight: "5px", height: "18px" }}
            onClick={toggleDisplay}
          />
          {t("Show active elements only")}
        </div>
      </div>
    );
  }

  // rebuild the tree each time... applying filters (search and focus)
  const theme_tree = filterThemeTree(
    themes_s,
    search,
    focus_theme_list,
    mode.display,
    active_display_list,
  );

  return (
    <>
      <div className="c-container_search-bar">
        <SearchBar
          value={search}
          handleChange={(e) => setSearch(e.target.value)}
        />
      </div>
      {view}
      <div
        className={`c-container_infos main-data container-focus-mode-${mode.editable ? "edit" : "readonly"}`}
      >
        <div className="focus-theme-list" ref={ref}>
          {theme_tree.map((t) => (
            <ThemeCard
              key={t.id}
              theme={t}
              toggleStar={toggleStar}
              toggleNotification={toggleNotification}
              focus={focus}
            />
          ))}
        </div>
      </div>
    </>
  );
}

export default ThemeFocus;
